import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { FEATURE_ACCESS_CHANGED_EVENT } from '../../hooks/useFeatureAccess';
import './BrandFeatureLocks.css';

/**
 * Admin → Brands → RiskLo-wide settings. Switches that apply to every brand and
 * every user at once (e.g. the intro tour). Saved immediately; staging and
 * production are set separately.
 */
export default function GlobalSettingsPanel() {
  const [features, setFeatures] = useState([]);
  const [allTiers, setAllTiers] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.adminFeatureAccess, { headers: getBearerAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not load settings');
      setFeatures((data.features || []).filter((f) => f.group === 'Global'));
      setAllTiers((data.tiers || []).map((t) => t.id));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (f) => {
    const on = f.tiers?.length > 0;
    setSavingKey(f.key);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.adminFeatureAccessUpdate(f.key), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ tiers: on ? [] : allTiers }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || 'Save failed');
      await load();
      window.dispatchEvent(new Event(FEATURE_ACCESS_CHANGED_EVENT));
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  if (!features.length && !error) return null;

  return (
    <section className="ae-card">
      <h2 className="ae-card-title">RiskLo-wide settings</h2>
      <p className="ae-sub">
        These apply to every brand and every user at once. They save straight away, and staging and
        production are set separately.
      </p>
      {error ? <div className="ae-error" role="alert">{error}</div> : null}
      <ul className="bfl-list">
        {features.map((f) => {
          const on = f.tiers?.length > 0;
          return (
            <li key={f.key}>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                className={`bfl-item${on ? '' : ' is-locked'}`}
                onClick={() => toggle(f)}
                disabled={savingKey === f.key}
                title={f.description || ''}
              >
                <span className="bfl-switch" aria-hidden="true"><span /></span>
                <span className="bfl-label">{f.label}</span>
                <span className="bfl-state">{savingKey === f.key ? 'Saving…' : on ? 'On' : 'Off'}</span>
              </button>
              {f.description ? <small className="bfl-desc">{f.description}</small> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
